import { Configuration } from './generated-api/runtime';
import type { Middleware } from './generated-api/runtime';
import { normalizeApiBaseUrl, getApiContextPath } from './utils/url-normalization';
import { resolveSessionIdForRequests } from './session-id-store';
import { recordPdfApiOutcome } from './security-dashboard-pdf-network-log';
import { logger } from './logger';

export interface CsrfTokenResponse {
  token: string;
}

/** Set by {@link primeCsrfToken} so CsrfMiddleware instances created earlier still attach X-XSRF-TOKEN. */
let primedCsrfTokenShared: string | null = null;

export function getPrimedCsrfToken(): string | null {
  return primedCsrfTokenShared;
}

export function clearPrimedCsrfToken(): void {
  primedCsrfTokenShared = null;
}

/**
 * Pre-login public auth API paths must not receive injected Authorization, X-Session-Id,
 * or X-XSRF-TOKEN (stale session would make Aegis reject discovery before login).
 * Matches .../v1/auth/discover/... and .../v1/auth/csrf-token
 */
const PUBLIC_AUTH_PATH_RE = /\/v1\/auth\/(discover(\/|$)|csrf-token($|\/|\?))/;
/** POST /api/v1/sessions — minting a session; never attach stale X-Session-Id. */
const SESSION_CREATE_PATH_RE = /\/v1\/sessions\/?(\?|$)/;

/**
 * Permission / feature / intermittent-data 403s that must NOT trigger configurationChanged
 * or session refresh (HAR: empty-body 403 on cases/metrics while siblings succeed).
 */
const PERMISSION_OR_DATA_403_PATH_RE =
  /\/v1\/(compliance\/|integration\/usage\/|customers(\/|$|\?)|cases(\/|$|\?))/i;

const SESSION_INVALID_403_BODY_RE =
  /session.{0,40}(expired|revoked|invalid|not\s+found)|cleaned\s+up|please\s+log\s+in\s+again/i;

/** True when Aegis returned 401 with SESSION_INVALID (session expired / revoked). */
async function readSessionInvalidFrom401(response: Response): Promise<{ invalid: boolean; message?: string }> {
  if (response.status !== 401) {
    return { invalid: false };
  }
  try {
    const body = await response.clone().json().catch(() => null);
    if (body && (body as { errorCode?: string }).errorCode === 'SESSION_INVALID') {
      return { invalid: true, message: (body as { message?: string }).message };
    }
  } catch {
    /* ignore */
  }
  return { invalid: false };
}

/**
 * Only treat 403 as session-invalidating when the body clearly says so.
 * Empty-body intermittent 403s (cases/metrics) are NOT session failure.
 */
async function readSessionInvalidating403(
  response: Response
): Promise<{ invalid: boolean; message?: string }> {
  if (response.status !== 403) {
    return { invalid: false };
  }
  try {
    const text = await response.clone().text().catch(() => '');
    if (!text || !text.trim()) {
      return { invalid: false };
    }
    let message = text;
    try {
      const body = JSON.parse(text) as { message?: string; errorCode?: string; error?: string };
      message = body.message || body.error || body.errorCode || text;
      if (body.errorCode === 'SESSION_INVALID') {
        return { invalid: true, message: body.message };
      }
    } catch {
      /* plain text body */
    }
    if (SESSION_INVALID_403_BODY_RE.test(message)) {
      return { invalid: true, message };
    }
  } catch {
    /* ignore */
  }
  return { invalid: false };
}

export function isPublicAuthApiRequestUrl(urlStr: string): boolean {
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://placeholder.local';
    const u = new URL(urlStr, base);
    return PUBLIC_AUTH_PATH_RE.test(u.pathname);
  } catch {
    return false;
  }
}

/** Exported for tests — cases/metrics/customers/compliance 403s must not escalate to auth refresh. */
export function shouldIgnoreAuthz403ForUrl(urlStr: string): boolean {
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://placeholder.local';
    const u = new URL(urlStr, base);
    return PERMISSION_OR_DATA_403_PATH_RE.test(u.pathname);
  } catch {
    return /\/v1\/cases/i.test(urlStr);
  }
}

function isSessionCreateRequest(url: string, method?: string): boolean {
  if (method !== 'POST') return false;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://placeholder.local');
    return SESSION_CREATE_PATH_RE.test(u.pathname);
  } catch {
    return SESSION_CREATE_PATH_RE.test(url);
  }
}

export class CsrfMiddleware implements Middleware {
  private csrfToken: string | null = null;
  /** Parallel POSTs share one in-flight CSRF fetch instead of N GET /auth/csrf-token calls. */
  private csrfTokenFetchInFlight: Promise<void> | null = null;
  /**
   * Fallback token captured at construction time. Used ONLY when storage has nothing yet (e.g. the
   * very first request of a brand-new instance created inline with a token that has not been
   * persisted). See getCurrentToken() - SECURITY (pentest finding C01 core fix): every subsequent
   * request must use the LIVE token from storage, never this stale snapshot, because Aegis mints a
   * brand-new short-lived session access token on session create/switch/refresh and this snapshot
   * would otherwise go stale within ~10 minutes (AegisSessionTokenService.DEFAULT_TTL_SECONDS).
   */
  private jwtToken: string;
  private basePath: string;

  constructor(jwtToken: string, basePath: string = 'http://localhost:8080') {
    this.jwtToken = jwtToken;
    this.basePath = basePath;
  }

  /**
   * Resolve the token to use for THIS request. Always prefers the live value from storage (which
   * auth.tsx/session-management.ts keep up to date with the current Aegis-signed session access
   * token) over the constructor-time snapshot.
   */
  private getCurrentToken(): string {
    const stored = readStoredAuthToken();
    if (stored && stored.access_token) {
      return stored.access_token;
    }
    return this.jwtToken;
  }

  async pre(context: { url: string; init: RequestInit }): Promise<void> {
    // Normalize accidental double /api in URLs (e.g., http://host/api/api/v1 -> http://host/api/v1)
    try {
      const u = new URL(context.url, window.location.origin);
      if (/\/api\/api\//.test(u.pathname)) {
        u.pathname = u.pathname.replace('/api/api/', '/api/');
        context.url = u.toString();
      }
    } catch {}

    // Validate token before making request (non-blocking)
    try {
      await validateAndRefreshToken();
    } catch (e) {
      logger.warn(' [CSRF] Token validation encountered an error, continuing');
    }

    if (isPublicAuthApiRequestUrl(context.url)) {
      return;
    }

    const isSessionCreate = isSessionCreateRequest(context.url, context.init.method);

    // Always add Authorization header with the LIVE current token (never log the value)
    context.init.headers = {
      ...context.init.headers,
      'Authorization': `Bearer ${this.getCurrentToken()}`,
      'Content-Type': 'application/json'
    };

    // Add X-Session-Id header for authenticated requests (not session create).
    if (!isSessionCreate) {
      try {
        const sid = resolveSessionIdForRequests();
        if (sid) {
          context.init.headers = {
            ...context.init.headers,
            'X-Session-Id': sid
          };
        }
      } catch (error) {
        logger.error(' [CSRF] Error resolving session id for request');
      }
    }

    // Add CSRF token for state-changing operations (never log token or headers).
    // Always await ensureCsrfToken so we don't race ahead of an in-flight CSRF fetch.
    // For session create: if no token is available yet, still allow the request through —
    // Spring may Set-Cookie: XSRF-TOKEN on the 403 response (credentials:include), and
    // auth retries; throwing here hangs login when GET /csrf-token returns success without a token.
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(context.init.method || '')) {
      await this.ensureCsrfToken();

      if (this.csrfToken) {
        context.init.headers = {
          ...context.init.headers,
          'X-XSRF-TOKEN': this.csrfToken
        };
      } else if (isSessionCreate) {
        logger.warn(' [CSRF] Session create proceeding without X-XSRF-TOKEN; auth layer should retry after Set-Cookie');
      }
    }
  }


  private async ensureCsrfToken(): Promise<void> {
    if (this.csrfToken) return;
    if (!this.csrfTokenFetchInFlight) {
      this.csrfTokenFetchInFlight = this.getCsrfToken().finally(() => {
        this.csrfTokenFetchInFlight = null;
      });
    }
    await this.csrfTokenFetchInFlight;
  }

  // Get CSRF token from cookie, shared prime, or fetch if not available
  private async getCsrfToken(): Promise<void> {
    // First try to get CSRF token from cookie
    const cookies = document.cookie.split(';');
    const xsrfCookie = cookies.find(cookie => cookie.trim().startsWith('XSRF-TOKEN='));

    if (xsrfCookie) {
      this.csrfToken = xsrfCookie.split('=').slice(1).join('=');
      return;
    }

    if (primedCsrfTokenShared) {
      this.csrfToken = primedCsrfTokenShared;
      return;
    }

    // If no cookie, fetch from endpoint
    const contextPath = getApiContextPath();
    const csrfUrl = `${this.basePath}${contextPath}/v1/auth/csrf-token`;

    try {
      const response = await fetch(csrfUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getCurrentToken()}` // Live current token for authentication
        },
        credentials: 'omit' // Don't send cookies (like aegis_session_id) to prevent 403 errors
      });

      if (response.ok) {
        const data: any = await response.json();

        if (data.success && data.token) {
          this.csrfToken = data.token;
          primedCsrfTokenShared = data.token;
        } else if (data.success && !data.token) {
          // CSRF not required for this operation
          return;
        } else {
          logger.error(' [CSRF] Unexpected CSRF token response format');
          throw new Error('Unexpected CSRF token response');
        }
      } else if (response.status === 403) {
        // Endpoint may reject requests with session cookies — continue without CSRF
        logger.warn(' [CSRF] CSRF token endpoint returned 403; continuing without CSRF token');
        return;
      } else {
        logger.error(' [CSRF] Failed to get CSRF token, status:', response.status);
        return;
      }
    } catch (error) {
      logger.error(' [CSRF] Exception while fetching CSRF token');
      // Don't throw - allow request to proceed without CSRF token (except session create, gated in pre())
    }
  }

  // Clear CSRF token (useful for logout)
  public clearCsrfToken(): void {
    this.csrfToken = null;
  }

  // Refresh CSRF token (useful when token expires)
  public async refreshCsrfToken(): Promise<void> {
    this.csrfToken = null;
    this.csrfTokenFetchInFlight = null;
    await this.ensureCsrfToken();
  }

  private async maybeEscalateAuthzFailure(
    status: number,
    url: string,
    response: Response | undefined,
    source: 'post' | 'onError'
  ): Promise<void> {
    if (status !== 401 && status !== 403) return;
    if (isPublicAuthApiRequestUrl(url)) return;

    if (status === 401 && response) {
      const { invalid, message } = await readSessionInvalidFrom401(response);
      if (invalid) {
        try {
          window.dispatchEvent(
            new CustomEvent('sessionInvalid', { detail: { source: 'http', url, message } })
          );
        } catch {}
        return;
      }
      // Non-SESSION_INVALID 401: nudge token renewal / config refresh
      this.dispatchConfigurationRefresh(source);
      return;
    }

    // 403: ignore known permission/data endpoints (cases, metrics, customers, …)
    if (shouldIgnoreAuthz403ForUrl(url)) {
      logger.debug(' [CSRF] Ignoring permission/data 403 (no auth refresh)', { url });
      return;
    }

    // 403 elsewhere: only escalate when body clearly indicates session invalidation
    if (response) {
      const { invalid, message } = await readSessionInvalidating403(response);
      if (invalid) {
        try {
          window.dispatchEvent(
            new CustomEvent('sessionInvalid', { detail: { source: 'http', url, message } })
          );
        } catch {}
        return;
      }
    }

    logger.debug(' [CSRF] Non-session 403 ignored (no configurationChanged)', { url });
  }

  private dispatchConfigurationRefresh(source: 'post' | 'onError'): void {
    logger.debug(` [CSRF] Authorization change detected (${source}); triggering configuration refresh`);
    try {
      const stored =
        (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('authToken') : null) ||
        (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null);
      if (stored) {
        const accessToken = JSON.parse(stored).access_token;
        if (accessToken) {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const timeUntilExpiry = payload.exp * 1000 - Date.now();
          if (timeUntilExpiry <= 0) {
            window.dispatchEvent(
              new CustomEvent('tokenExpiringSoon', {
                detail: { timeUntilExpiry, forceRenewal: true },
              })
            );
          }
        }
      }
    } catch {}
    try { window.dispatchEvent(new Event('configurationChanged')); } catch {}
    try { localStorage.setItem('availableTenantsVersion', String(Date.now())); } catch {}
  }

  // Post-response hook: only escalate true session invalidation — not intermittent data 403s.
  async post(context: { url: string; init: RequestInit; response: Response }): Promise<void> {
    try {
      const { response, url, init } = context;
      if (!response) return;
      await recordPdfApiOutcome({
        url,
        method: init?.method,
        status: response.status,
        response,
      });
      await this.maybeEscalateAuthzFailure(response.status, url, response, 'post');
    } catch {
      logger.warn(' [CSRF] post hook error');
    }
  }

  // Error hook: same policy as post().
  async onError(context: { url: string; init: RequestInit; error: any; response?: Response }): Promise<void> {
    try {
      const status = context?.response?.status;
      await recordPdfApiOutcome({
        url: context?.url ?? '',
        method: context?.init?.method,
        status: status ?? null,
        response: context?.response,
        note: status == null ? 'network-error' : undefined,
      });
      if (status == null) return;
      await this.maybeEscalateAuthzFailure(status, context?.url ?? '', context?.response, 'onError');
    } catch {
      logger.warn(' [CSRF] onError hook handling failed');
    }
  }
}

// Add token validation and renewal logic
const readStoredAuthToken = (): { access_token?: string } | null => {
  try {
    const stored =
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('authToken') : null) ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

let lastTokenExpiringSoonDispatchAt = 0;
const TOKEN_EXPIRING_SOON_DISPATCH_COOLDOWN_MS = 5000;

const validateAndRefreshToken = async (): Promise<boolean> => {
  try {
    const parsed = readStoredAuthToken();
    if (!parsed) {
      return true;
    }

    const accessToken = parsed.access_token;

    if (!accessToken) {
      return true;
    }

    // Decode JWT to check expiration (do not log token material)
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      // If token expires in less than 5 minutes (or is already expired), trigger renewal (non-blocking)
      if (timeUntilExpiry < 5 * 60 * 1000) {
        const alreadyExpired = timeUntilExpiry <= 0;
        const now = Date.now();
        if (now - lastTokenExpiringSoonDispatchAt >= TOKEN_EXPIRING_SOON_DISPATCH_COOLDOWN_MS) {
          lastTokenExpiringSoonDispatchAt = now;
          logger.debug(
            alreadyExpired
              ? ' [TOKEN VALIDATION] Access token expired; forcing renewal'
              : ' [TOKEN VALIDATION] Access token expiring soon; triggering renewal'
          );

          window.dispatchEvent(
            new CustomEvent('tokenExpiringSoon', {
              detail: { timeUntilExpiry, forceRenewal: alreadyExpired },
            })
          );
        }
      }

      return true;
    } catch {
      logger.warn(' [TOKEN VALIDATION] Could not decode access token');
      return true; // Allow the call to proceed
    }
  } catch {
    logger.warn(' [TOKEN VALIDATION] Error validating token');
    return true; // Allow the call to proceed
  }
};

// Helper function to create a configuration with CSRF middleware
export function createCsrfEnabledConfig(jwtToken: string, basePath?: string): Configuration {
  // Use centralized URL normalization utility with configurable context path
  const contextPath = getApiContextPath();
  const { apiBase, rootBase } = normalizeApiBaseUrl(basePath || 'http://localhost:8080', { contextPath });

  const csrfMiddleware = new CsrfMiddleware(jwtToken, rootBase);

  // CRITICAL FIX: Use apiBase as basePath since regenerated client now has consistent /v1/... paths
  // The regenerated client uses paths like /v1/cases, so we need the base URL to include /api
  const config = new Configuration({
    basePath: apiBase,
    middleware: [csrfMiddleware],
    credentials: 'include',
  });

  return config;
}

/**
 * Warm CSRF before a burst of state-changing API calls (e.g. headless PDF widget metrics).
 * Sets `XSRF-TOKEN` cookie when the endpoint returns a token so {@link CsrfMiddleware} can attach it.
 * Soft-fails (resolves) on network/403 — callers still proceed; metrics retries cover residual races.
 */
export async function primeCsrfToken(
  accessToken: string,
  basePath?: string
): Promise<{ primed: boolean; token?: string }> {
  if (typeof window === 'undefined' || !accessToken?.trim()) {
    return { primed: false };
  }

  // Already have a cookie — middleware will pick it up.
  try {
    const existing = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith('XSRF-TOKEN='));
    if (existing) {
      const token = existing.slice('XSRF-TOKEN='.length);
      if (token) {
        primedCsrfTokenShared = token;
        return { primed: true, token };
      }
    }
  } catch {
    /* ignore */
  }

  const contextPath = getApiContextPath();
  const { rootBase } = normalizeApiBaseUrl(basePath || 'http://localhost:8080', { contextPath });
  const csrfUrl = `${rootBase}${contextPath}/v1/auth/csrf-token`;

  try {
    const response = await fetch(csrfUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'omit',
    });

    if (response.ok) {
      const data: any = await response.json();
      if (data?.success && data?.token) {
        const token = String(data.token);
        primedCsrfTokenShared = token;
        try {
          document.cookie = `XSRF-TOKEN=${token}; path=/; SameSite=Lax`;
        } catch {
          /* ignore */
        }
        return { primed: true, token };
      }
      // success without token — CSRF not required
      return { primed: true };
    }

    if (response.status === 403) {
      logger.warn(' [CSRF] primeCsrfToken: endpoint returned 403; continuing without CSRF');
      return { primed: false };
    }

    logger.warn(' [CSRF] primeCsrfToken: unexpected status', response.status);
    return { primed: false };
  } catch (e) {
    logger.warn(' [CSRF] primeCsrfToken failed; continuing without CSRF', e);
    return { primed: false };
  }
}
